const STORAGE_KEY = 'pivot.audio.volume';
const DEFAULT_VOLUME = 0.6;

type VolumeListener = (volume: number) => void;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function readInitialVolume(): number {
  if (typeof window === 'undefined') {
    return DEFAULT_VOLUME;
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (!stored) return DEFAULT_VOLUME;
    const parsed = parseFloat(stored);
    if (Number.isFinite(parsed)) {
      return clamp(parsed, 0, 1);
    }
  } catch (error) {
    console.warn('[audio-manager] Failed to read stored volume', error);
  }

  return DEFAULT_VOLUME;
}

let volume = readInitialVolume();
const listeners = new Set<VolumeListener>();

function persistVolume(next: number): void {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, String(next));
  } catch (error) {
    console.warn('[audio-manager] Failed to persist volume', error);
  }
}

export const audioManager = {
  getVolume(): number {
    return volume;
  },

  setVolume(next: number): void {
    const clamped = clamp(next, 0, 1);
    if (clamped === volume) {
      return;
    }
    volume = clamped;
    persistVolume(volume);
    listeners.forEach(listener => {
      try {
        listener(volume);
      } catch (error) {
        console.error('[audio-manager] Volume listener failed', error);
      }
    });
  },

  onVolumeChange(listener: VolumeListener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }
} as const;

